/**
 * Trial lifecycle math (04 §4.5). All lifecycle facts are computed from stored
 * timestamps — never from drift-prone "day counter" fields. Shared by the
 * banner and its tests so the day-N logic has one home.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const BASE_TRIAL_DAYS = 14; // active window before freeze
const EXTENSION_DAYS = 7; // one extension = 7 days
const MAX_EXTENSIONS = 2; // → up to 14 extra days

/** Coerce a Firestore Timestamp | Date | ms | ISO string to a Date (or null). */
export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Derive the display state for a trial org's banner from its org doc fields.
 * @param {object} orgData org document (trial*, trialFrozen, …)
 * @param {Date}   [now]   injectable clock for tests
 */
export function deriveTrialState(orgData = {}, now = new Date()) {
  const flavor = orgData.trialFlavor === "sample" ? "sample" : "blank";
  const frozen = orgData.trialFrozen === true;
  const extendedDays = Number(orgData.trialExtendedDays) || 0;
  const totalDays = BASE_TRIAL_DAYS + extendedDays;

  const startedAt = toDate(orgData.trialStartedAt);
  // Day 1 is the day the trial started; clamp into [1, totalDays].
  let dayN = 1;
  if (startedAt) {
    const elapsed = Math.floor((now.getTime() - startedAt.getTime()) / DAY_MS);
    dayN = Math.min(Math.max(elapsed + 1, 1), totalDays);
  }

  const purgeAt = toDate(orgData.trialPurgeAt);
  // Sample trials are erase-only (never promote); only blank trials can extend.
  const canExtend = flavor === "blank" && extendedDays < MAX_EXTENSIONS * EXTENSION_DAYS;
  const canPromote = flavor === "blank";

  return { flavor, frozen, extendedDays, totalDays, dayN, startedAt, purgeAt, canExtend, canPromote };
}

export const TRIAL_CONSTANTS = { BASE_TRIAL_DAYS, EXTENSION_DAYS, MAX_EXTENSIONS };

// httpsCallable options for trial seed/erase calls — building a large sample
// runs well past the SDK's 70s default.
export const SEED_CALL_OPTS = { timeout: 240000 };
